import {
	fetchBaseQuery,
	createApi
} from "@reduxjs/toolkit/query/react";
import { setCredentials, logout } from "./features/auth/authSlice";
import type {
	BaseQueryApi,
	FetchArgs,
	FetchBaseQueryError,
	FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { getPersistor } from "./persistorRef";
import type { QueryReturnValue } from "@reduxjs/toolkit/query";

import i18n from "src/i18n";
import { apiBaseUrl, apiPublicBaseUrl } from "src/config";

export const baseUrl = apiBaseUrl;

const baseQuery = fetchBaseQuery({
	baseUrl,
	//credentials: 'include', //to send cookie, not useful if we don't keep refresh token there
	prepareHeaders: (headers, { getState }) => {
		const state = getState() as { auth: { accessToken: string | null } };
		const token = state.auth.accessToken;
		if (token) headers.set("authorization", `Bearer ${token}`);

		if (!headers.has("Accept-Language"))
			headers.set("Accept-Language", i18n.language);

		return headers;
	},
});

const baseQueryWithReauth = async (
	args: string | FetchArgs,
	api: BaseQueryApi,
	extraOptions: {},
) => {
	let result = await baseQuery(args, api, extraOptions);
	const state = api.getState() as { auth: { accessToken: string | null; refreshToken: string | null; user: unknown } };

	if (result?.error?.status === 401 || result?.error?.status === 403) {
		const refreshToken = state.auth.refreshToken;
		const query = {
			url: "/accounts/token/refresh/",
			method: "POST",
			body: { refresh: refreshToken },
		};
		const refreshResult: QueryReturnValue<
			{ access_token: string, refresh_token: string, uid: string },
			FetchBaseQueryError,
			FetchBaseQueryMeta
		> = (await baseQuery(query, api, extraOptions)) as QueryReturnValue<
			{ access_token: string, refresh_token: string, uid: string },
			FetchBaseQueryError,
			FetchBaseQueryMeta
		>;
		if (refreshResult?.data) {
			const user = state.auth.user;
			//store the new token
			api.dispatch(
				setCredentials({
					accessToken: refreshResult.data?.access_token,
					refreshToken: refreshResult.data?.refresh_token,
				}),
			);
			//retry the original query
			result = await baseQuery(args, api, extraOptions);
		} else {
			api.dispatch(logout());
			api.dispatch(apiSlice.util.resetApiState());
			getPersistor()?.purge();
		}
	}
	return result;
};

const tagTypes = ['FakeTag', 'Activities', 'ActivityResults', 'AnnualCropland', 'AnnualCroplandDefaults', 'LandUseChange', 'Grassland', "GrasslandDefaults", 'SmallFisheries', 'SmallFisheriesDefaults', 'LargeFisheries', 'LargeFisheriesDefaults', 'DetailedProjects', 'FastProjects', 'FundingAgencies', 'ExecutingAgencies', 'PerennialCropland', 'Livestock', 'FloodedRice', 'FloodedRiceDefaults', 'FloodedRiceSeasonDefaults', 'Aquaculture', 'AquacultureDefaults', 'InputEntries', 'InputEntriesDefaults', 'ModuleResults', 'ModuleHistory', 'FloodedSeasons', 'PerennialSeasons', 'Invitations', 'ProjectUsers', 'Groups', 'Users', 'ModuleTypes', 'LandUseTypes', 'InputTypes', 'FuelTypes', 'MacroInputTypes', 'MacroFuelTypes', 'FuelUseTypes', 'ForestManagement', 'ForestManagementDefaults', 'ForestDisturbance', 'ForestDisturbanceDefaults', 'SetAside', 'SetAsideDefaults', 'ModuleNotes', 'ModuleComments', 'OtherLand', 'OtherLandDefaults', 'Waterbodies', 'WaterbodiesDefaults', 'Electricities', 'ElectricitiesDefaults', 'Fuels', 'FuelsDefaults', 'Settlements', 'SettlementsDefaults', 'Roads', 'RoadsDefaults', 'CoastalWetlands', 'CoastalWetlandsDefaults', 'Buildings', 'BuildingsDefaults', 'OrganicSoil', 'OrganicSoilDefaults', 'IrrigationSystems', 'IrrigationSystemsDefaults', 'IrrigationPhases', 'IrrigationPhasesDefaults', 'Modules', "Others", 'OthersDefaults', "Energies", 'EnergyEntries', 'EnergyEntriesDefaults', "Inputs", "Irrigations", 'ModulesStatuses', 'PerennialCroplandDefaults', 'LivestockDefaults', 'FastActivities', 'Storages', 'StorageEntries', 'StorageEntriesDefaults', 'Transports', 'TransportEntries', 'TransportEntriesDefaults', 'Processings', 'ProcessingEntries', 'ProcessingEntriesDefaults', 'Packagings', 'PackagingEntries', 'PackagingEntriesDefaults', 'ProjectAttachments', 'Tags', 'ProjectTags', 'ClimateTypes', 'MoistureTypes', 'SoilTypes', 'Countries', 'GWPSources', 'ProjectStatuses', 'ChangeRates', 'ActivityStatuses', 'ProjectResults', 'AsyncJobs', 'NotificationPreferences'] as const;
export type TagTypes = (typeof tagTypes)[number];

export const apiSlice = createApi({
	baseQuery: baseQueryWithReauth,
	endpoints: builder => ({}),
	tagTypes
	/* ,
 extractRehydrationInfo(action, { reducerPath }) {
  if (action.type === REHYDRATE) {
   return action.payload[reducerPath]
  }
 }, */,
});

export const basePublicUrl = apiPublicBaseUrl;

// A simpler base query that doesn't try to authenticate
const publicBaseQuery = fetchBaseQuery({
	baseUrl: basePublicUrl,
	prepareHeaders: (headers) => {
		if (!headers.has("Accept-Language"))
			headers.set("Accept-Language", i18n.language);
		return headers;
	},
});

// Create a separate API slice for public endpoints
export const publicApiSlice = createApi({
	reducerPath: 'publicApi',
	baseQuery: publicBaseQuery,
	endpoints: builder => ({}),
	tagTypes: tagTypes
});
